// C:\Projects\texe\texe-web\src\components\NotificationItem.tsx
import { useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import type { NotificationItem as Notif } from "../lib/api";
import * as api from "../lib/api";
import Avatar from "./Avatar";
import ImageModal from "./ImageModal";
import { formatTimeAgo } from "../lib/timeAgo";

function formatMessage(n: Notif) {
  const name = n.actor?.username ? `@${n.actor.username}` : "Bir kullanıcı";
  switch (n.type) {
    case "like": return `${name} gönderini/yorumunu beğendi.`;
    case "comment": return `${name} gönderine yorum yaptı.`;
    case "follow": return `${name} seni takip etmeye başladı.`;
    case "comment_like": return `${name} yorumunu beğendi.`;
    case "mention": return `${name} senden bahsetti.`;
    case "kabile_invite": return `${name} seni "${n.kabile?.name || 'kabile'}" kabilesine davet etti.`;
    case "kabile_join_request": return n.text || `${name} kabilenize katılmak istiyor.`;
    default: return `${name}`;
  }
}

export default function NotificationItem({
  item,
  onOpen,
  justArrived,
}: {
  item: Notif;
  onOpen?: (n: Notif) => void;
  justArrived?: boolean;
}) {
  const navigate = useNavigate();
  const [showImg, setShowImg] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const href = useMemo(() => {
    if (item.type === "kabile_invite") return "#"; // Özel işlem - butonlarla yönetiliyor
    if (item.type === "kabile_join_request") return "/savkabile/my"; // Kabile yönetim sayfasına
    if (item.comment?.postId) {
      const postId = String(item.comment.postId);
      const commentId = item.comment.id ? String(item.comment.id) : "";
      const q = commentId ? `?c=${encodeURIComponent(commentId)}` : "";
      return `/p/${encodeURIComponent(postId)}${q}`;
    }
    if (item.post) return `/p/${encodeURIComponent(String(item.post.id))}`;
    if (item.actor) return `/u/${encodeURIComponent(item.actor.username || item.actor.id)}`;
    return "/";
  }, [item]);

  const label = useMemo(() => formatMessage(item), [item]);

  const openTarget = useCallback(() => {
    if (item.type === "kabile_invite") return; // Prevent click for invites (handled by buttons)
    // kabile_join_request için tıklamaya izin ver - /savkabile/my'ye yönlendirecek
    onOpen?.(item);
    navigate(href);
  }, [href, item, navigate, onOpen]);

  const handleAcceptInvite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsProcessing(true);
    try {
      const result = await api.acceptKabileInvite(item.id);
      alert("Kabileye katıldınız!");
      navigate(`/savkabile/my`);
    } catch (error: any) {
      alert(error.message || "Davet kabul edilirken bir hata oluştu");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRejectInvite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsProcessing(true);
    try {
      await api.rejectKabileInvite(item.id);
      window.location.reload(); // Refresh notifications
    } catch (error: any) {
      alert(error.message || "Davet reddedilirken bir hata oluştu");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleApproveJoinRequest = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsProcessing(true);
    try {
      await api.approveJoinRequest(item.id);
      alert("Kullanıcı kabileye eklendi!");
      window.location.reload(); // Refresh notifications
    } catch (error: any) {
      alert(error.message || "Talep onaylanırken bir hata oluştu");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRejectJoinRequest = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsProcessing(true);
    try {
      await api.rejectJoinRequest(item.id);
      window.location.reload(); // Refresh notifications
    } catch (error: any) {
      alert(error.message || "Talep reddedilirken bir hata oluştu");
    } finally {
      setIsProcessing(false);
    }
  };

  const avatarSmall = item.actor?.avatarUrlSmall || item.actor?.avatarUrl || null;
  const avatarBig = item.actor?.avatarUrl || item.actor?.avatarUrlSmall || null;
  const isUnread = !item.read;

  const snippet = (s?: string | null) => (s && s.length > 120 ? s.slice(0, 120) + "…" : s || "");
  const mentionSnippet =
    (item.comment?.text && snippet(item.comment.text)) ||
    (item.post?.text && snippet(item.post.text)) ||
    "";

  const ago = formatTimeAgo(item.createdAt, "tr");

  return (
    <div
      onClick={openTarget}
      role="button"
      tabIndex={0}
      aria-label={label}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openTarget(); }
      }}
      className={`notif-item ${justArrived ? "notif-new" : ""}`}
      style={{
        display: "grid",
        gridTemplateColumns: "48px 1fr",
        gap: 12,
        padding: "10px 12px",
        borderBottom: "1px solid #2c2c2c",
        cursor: "pointer",
        background: isUnread ? "rgba(0, 120, 255, 0.07)" : "transparent",
      }}
      title={new Date(item.createdAt).toLocaleString()}
    >
      <div
        onClick={(e) => { e.stopPropagation(); if (avatarBig) setShowImg(true); }}
        style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
        title={avatarBig ? "Avatarı büyüt" : undefined}
      >
        <Avatar
          src={avatarSmall ?? undefined}
          alt={item.actor?.username || "avatar"}
          size={40}
          variant={isUnread ? "ring" : "default"}
          ringColor="#0078ff"
        />
      </div>

      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <strong style={{ whiteSpace: "nowrap" }}>
            {item.actor?.username ? `@${item.actor.username}` : "Kullanıcı"}
          </strong>
        </div>

        <div style={{ marginTop: 4, lineHeight: 1.4 }}>
          {label}{" "}
          {item.type === "comment" && item.comment?.text ? (
            <span style={{ opacity: 0.8 }}>— <q>{snippet(item.comment.text)}</q></span>
          ) : null}
          {item.type === "like" && item.post?.text ? (
            <span style={{ opacity: 0.8 }}>— <q>{snippet(item.post.text)}</q></span>
          ) : null}
          {item.type === "comment_like" && item.comment?.text ? (
            <span style={{ opacity: 0.8 }}>— <q>{snippet(item.comment.text)}</q></span>
          ) : null}
          {item.type === "mention" && mentionSnippet ? (
            <span style={{ opacity: 0.8 }}>— <q>{mentionSnippet}</q></span>
          ) : null}
        </div>

        {item.type === "kabile_invite" && !item.read && (
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button
              onClick={handleAcceptInvite}
              disabled={isProcessing}
              style={{
                padding: "6px 16px",
                background: "linear-gradient(135deg, #10b981, #059669)",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                cursor: isProcessing ? "not-allowed" : "pointer",
                fontWeight: 600,
                fontSize: "0.9rem",
                opacity: isProcessing ? 0.6 : 1,
              }}
            >
              ✓ Kabul Et
            </button>
            <button
              onClick={handleRejectInvite}
              disabled={isProcessing}
              style={{
                padding: "6px 16px",
                background: "linear-gradient(135deg, #ef4444, #dc2626)",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                cursor: isProcessing ? "not-allowed" : "pointer",
                fontWeight: 600,
                fontSize: "0.9rem",
                opacity: isProcessing ? 0.6 : 1,
              }}
            >
              ✗ Reddet
            </button>
          </div>
        )}

        {item.type === "kabile_join_request" && !item.read && (
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button
              onClick={handleApproveJoinRequest}
              disabled={isProcessing}
              style={{
                padding: "6px 16px",
                background: "linear-gradient(135deg, #10b981, #059669)",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                cursor: isProcessing ? "not-allowed" : "pointer",
                fontWeight: 600,
                fontSize: "0.9rem",
                opacity: isProcessing ? 0.6 : 1,
              }}
            >
              ✓ Onayla
            </button>
            <button
              onClick={handleRejectJoinRequest}
              disabled={isProcessing}
              style={{
                padding: "6px 16px",
                background: "linear-gradient(135deg, #ef4444, #dc2626)",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                cursor: isProcessing ? "not-allowed" : "pointer",
                fontWeight: 600,
                fontSize: "0.9rem",
                opacity: isProcessing ? 0.6 : 1,
              }}
            >
              ✗ Reddet
            </button>
          </div>
        )}

        <div style={{ opacity: 0.7, fontSize: 12, marginTop: 4 }}>
          {ago}
        </div>
      </div>

      <ImageModal
        open={showImg}
        src={avatarBig ?? undefined}
        alt={item.actor?.username || "avatar"}
        caption={item.actor?.username ? `@${item.actor.username}` : undefined}
        onClose={() => setShowImg(false)}
      />
    </div>
  );
}
